import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  // local URL
  private readonly baseURL = environment.apiUrl;

  constructor(private http: HttpClient) { }

  // get element by id
  getById(controllerRoute: string, id: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-by-id?id=${id}`);
  }

  // get element by id, targeting a custom action route (e.g. "get-details")
  getByIdCustomAction(controllerRoute: string, action: string, id: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/${action}?id=${id}`);
  }

  // get all elements
  getAllFiltered(controllerRoute: string, status?: boolean | null): Observable<any> {
    let url = `${this.baseURL}/${controllerRoute}/get-all`;
    if (status !== null && status !== undefined) {
      url += `?status=${status}`;
    }

    return this.http.get<any>(url);
  }

  // get all elements
  getAll(controllerRoute: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseURL}/${controllerRoute}/get-all`);
  }

  // add a new item
  create(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/create`, item);
  }

  // update an existing item
  update(controllerRoute: string, item: any): Observable<any> {
    return this.http.put<any>(`${this.baseURL}/${controllerRoute}/update`, item);
  }

  // delete an item
  delete(controllerRoute: string, item: any): Observable<any> {
    return this.http.put<any>(`${this.baseURL}/${controllerRoute}/delete?id=${item.id}`, item);
  }

  // Fetch details with pagination and filters
  getDetails(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/details`, item);
  }

  updateStatus(controllerRoute: string, id: number, status: boolean): Observable<any> {
    return this.http.put<any>(`${this.baseURL}/${controllerRoute}/update-status/${id}`, status);
  }

  cancel(controllerRoute: string, id: number, userId: number): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/cancel/${id}?UserId=${userId}`, null);
  }

  // Get data depend on status
  getByStatus(controllerRoute: string, status: boolean | null): Observable<any> {
    if (status === null) {
      return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-all`);
    }
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-by-status?status=${status}`);
  }

  registerEmployee(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/register`, item);
  }

  updateEmployeeUserStatus(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/update/employee-status`, item);
  }

  checkIfEmployeeUserIsAlreadyExists(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/check/user/is-exists`, item);
  }

  getByIdAndStatus(endPointRoute: string, controllerRoute: string, id: number, status: boolean): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/${endPointRoute}?id=${id}&status=${status}`);
  }

  exportToExcel(item: any, type: String, sheetTitle: string): Observable<Blob> {
    return this.http.post<Blob>(`${this.baseURL}/export-to-excel/sheet?type=${type}&sheetTitle=${sheetTitle}`, item, {
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob' as 'json',
    });
  }

  //to download excel file
  downloadFile(response: Blob, fileName: string = 'exported_data.xlsx'): void {
    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  getEvaluationResults(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/get-result`, item);
  }

  getEvaluationResultsByCriteria(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/get-result-by-criteria`, item);
  }

  getEvaluationResultsByObject(controllerRoute: string, item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/${controllerRoute}/get-result-by-object`, item);
  }

  getByModuleIdAndStatus(controllerRoute: string, moduleId: number, status: boolean, roleId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-by-module-and-role-id/${status}/${moduleId}/${roleId}`);
  }


  GetAllByAction(controllerRoute: string, actionId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/${controllerRoute}/get-by-action?actionId=${actionId}`
    );
  }

  //system settings
  changePassword(item: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/User/change-password`, item);
  }

  getAuthoritiesWithChildrenByStatus(controllerRoute: string, status: boolean | null) {
    let url = `${this.baseURL}/${controllerRoute}/get-with-children-by-status`;
    if (status !== null && status !== undefined) {
      url += `?status=${status}`;
    }
    return this.http.get<any>(url);
  }
  
  getUsersByUserTypeId(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/User/get-by-user-type?userTypeId=${id}`);
  }

  AddWorkflowInstanceExecutionReassign(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/workflow/instance/execution/reassignment/create`, data);
  }

  UpdateWorkflowInstanceExecutionReassign(data: any): Observable<any> {
    return this.http.put<any>(`${this.baseURL}/workflow/instance/execution/reassignment/update`, data);
  }

  getAllActiveStatusCodes(stepId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/workflow/step/status/get-step-status-by-step-id/${stepId}`)
  }

  SaveWorkFlowNoteImages(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/ticket/photo/create`, data)
  }

  // get dropDrown list elements
  getDropDownList(controllerRoute: string, status: boolean): Observable<any> {
    return this.http.get<any[]>(`${this.baseURL}/${controllerRoute}/get-all?status=${status}`);
  }

  //InsertWorkflowIdentifiers
  SaveWorkflowIdentifiers(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/Workflow/Identifier/save-identifiers`, data);
  }

  getWorkflowIdentifiers(workflowId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/Workflow/Identifier/get-by-workflow?workflowId=${workflowId}`);
  }

  //InsertAttachmentTypeFileExtensions
  SaveAttachmentTypeFileExtensions(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/dms/attachment-class/save-file-extensions`, data);
  }

  getAvailableAttachmentTypeFileExtensions(attachmentClassId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/dms/attachment-class/get-available-by-attachment-type-id?attachmentClassId=${attachmentClassId}`);
  }

  getEmployeeDetails(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/Employee/details`, { params: { employeeId } });
  }

  getAuthorityDetails(authorityId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/Authority/get-details-by-id`, { params: { id: authorityId } });
  }

  getSubjectDetails(subjectId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/subject/details/${subjectId}`);
  }

  getSideMenuDetails(sideMenuId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/side/menu/details?id=${sideMenuId}`);
  }

  getEvaluationSubjectDetails(subjectId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/evaluation/subject/details/${subjectId}`);
  }

  getDropdownListRecords(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/user/get-dropdown-list`, data);
  }

  getByRoleId(controllerRoute: string, roleId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-by-role-id?roleId=${roleId}`);
  }

  aiDocumentReader(doc: any): Observable<any> {
    return this.http.post<any>(`${this.baseURL}/DocumentIntelligentReader/azure-extract`, doc);
  }

  deleteByQuery(controllerRoute: string, params: any): Observable<any> {
    const queryString = new URLSearchParams(params).toString();
    return this.http.delete<any>(`${this.baseURL}/${controllerRoute}?${queryString}`);
  }

  getByKey(controllerRoute: string, key: string) {
    return this.http.get<any>(`${this.baseURL}/${controllerRoute}/get-by-key`, {
      params: { Key: key }
    });
  }
  uploadFile(controllerRoute: string, formData: FormData) {
    return this.http.post<any>(`${environment.apiUrl}/${controllerRoute}`, formData);
  }
  getRolesByUserTypeId(userTypeId: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/Role/get-by-user-type-id?userTypeId=${userTypeId}`);
  }
  getChatAllowedRoles(status: boolean): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/Role/get-discussion-allowed-roles?status=${status}`);
  }
}
